/**
 * /sign-up — Story 1a + 1d + J-X13.
 *
 * react-hook-form + zod per CONVENTIONS guidance; server validation is the
 * source of truth, this client validation mirrors it for UX feedback.
 *
 * J-X13 (15-FG/Track-D): required ToS + Privacy acceptance checkbox below
 * submit. Checkbox starts UNCHECKED; submit disabled until checked. On
 * submit, `tos_version` + `privacy_version` are included in the API call
 * so the server can stamp them onto the user row.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { AuthCard } from '../../components/auth/auth-card.tsx';
import { Button, Input } from '@/components/ui';
import { authApi } from '../../state/auth/api.ts';
import { TOS_VERSION, PRIVACY_VERSION } from '@bimassess/db/legal-versions';

const schema = z.object({
  name: z.string().min(1, 'name is required'),
  email: z.string().email('enter a valid email'),
  password: z.string().min(10, 'password must be at least 10 characters'),
  confirmPassword: z.string(),
}).refine((v) => v.password === v.confirmPassword, {
  message: 'passwords do not match',
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof schema>;

export function SignUpPage(): JSX.Element {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    if (!consentAccepted) {
      setServerError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    const res = await authApi.signUp({
      email: values.email,
      password: values.password,
      name: values.name,
      tos_version: TOS_VERSION,
      privacy_version: PRIVACY_VERSION,
    });
    if (!res.ok) {
      // Anti-enumeration: never surface "account_exists" verbatim — translate
      // to a neutral string so the page doesn't confirm an email is in use.
      setServerError(
        res.error.code === 'account_exists'
          ? "We couldn't create that account. If you already have one, try signing in."
          : res.error.message || 'Something went wrong. Please try again.',
      );
      return;
    }
    // Post-sign-up role-based routing via RoleBasedRedirect component (15-frontend-gaps/04).
    navigate('/post-sign-in');
  });

  return (
    <AuthCard
      eyebrow="Sign up"
      title="Create your BIMAssess account."
      lede="Earn verifiable proof of Revit competency — practitioner-built assessments, employer-recognised credentials."
      footer={
        <>
          Already have an account?{' '}
          <Link className="text-ink underline hover:text-accent" to="/sign-in">
            Sign in
          </Link>
          .
        </>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <Input label="Full name" id="su-name" error={errors.name?.message} type="text" autoComplete="name" {...register('name')} />
        <Input label="Work email" id="su-email" error={errors.email?.message} type="email" autoComplete="email" {...register('email')} />
        <Input
          label="Password"
          id="su-password"
          helperText="At least 10 characters."
          error={errors.password?.message}
          type="password"
          autoComplete="new-password"
          {...register('password')}
        />
        <Input
          label="Confirm password"
          id="su-confirm"
          error={errors.confirmPassword?.message}
          type="password"
          autoComplete="new-password"
          {...register('confirmPassword')}
        />
        {serverError ? (
          <p className="font-sans text-small text-error" role="alert">
            {serverError}
          </p>
        ) : null}
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting || !consentAccepted}
          aria-disabled={!consentAccepted}
        >
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </Button>
        {/* J-X13 — required consent checkbox below the submit button, starts
            UNCHECKED. Pre-checking would invalidate explicit consent
            (Failure mode 5). Wraps the entire row as a <label> so click
            target spans label + checkbox. */}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={consentAccepted}
            onChange={(e) => setConsentAccepted(e.target.checked)}
            aria-required
            className="mt-1.5"
          />
          <span className="font-sans text-small text-ink">
            I have read and agree to the{' '}
            <Link to="/legal/terms" className="text-ink underline hover:text-accent" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/legal/privacy" className="text-ink underline hover:text-accent" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
      </form>
    </AuthCard>
  );
}
